import React from 'react';
import { Lead, LoanStage } from '../types';
import { AlertCircle, Home, Activity, SearchX } from 'lucide-react';
interface LeadTableProps {
  leads: Lead[];
}
const getStageColor = (stage: LoanStage) => {
  switch (stage) {
    case 'Lead Received':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    case 'Contact Made':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'Application Taken':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    case 'Waiting on Documentation':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'Docs Received':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'Pre-Approved':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'Accepted Offer':
      return 'bg-teal-50 text-teal-700 border-teal-200';
    case 'Loan Disclosed':
      return 'bg-cyan-50 text-cyan-700 border-cyan-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};
const formatCurrency = (value: number | null) => {
  if (value === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
};
const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};
export const LeadTable: React.FC<LeadTableProps> = ({
  leads
}) => {
  return <div className="flex-1 overflow-auto bg-white" data-id="element-55">
      <table className="min-w-max w-full text-left border-collapse" data-id="element-56">
        <thead className="bg-gray-50/80 backdrop-blur-sm sticky top-0 z-20" data-id="element-57">
          <tr data-id="element-58">
            <th className="sticky left-0 bg-gray-50/95 backdrop-blur-sm px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 z-30 shadow-[1px_0_0_0_#e5e7eb]" data-id="element-59">
              Borrower Name
            </th>
            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-60">
              Current Stage
            </th>
            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-61">
              Days in Stage
            </th>
            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-62">
              Next Action
            </th>
            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-63">
              Lead Date
            </th>
            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-64">
              App Date
            </th>
            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-65">
              Last Contact
            </th>
            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-66">
              Loan Details
            </th>
            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-67">
              Financials
            </th>
            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-68">
              Property
            </th>
            <th className="px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-69">
              Referral Source
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white" data-id="element-70">
          {leads.length === 0 ? <tr data-id="element-71">
              <td colSpan={11} className="px-5 py-16 text-center" data-id="element-72">
                <div className="flex flex-col items-center justify-center text-gray-500" data-id="element-73">
                  <div className="bg-gray-50 p-3 rounded-full mb-3" data-id="element-74">
                    <SearchX className="w-6 h-6 text-gray-400" data-id="element-75" />
                  </div>
                  <p className="text-sm font-medium text-gray-900" data-id="element-76">
                    No leads found
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-77">
                    Try adjusting your filters or search query.
                  </p>
                </div>
              </td>
            </tr> : leads.map(lead => <tr key={lead.id} className="hover:bg-gray-50/80 transition-colors group" data-id="element-78">
                <td className="sticky left-0 bg-white group-hover:bg-gray-50/80 px-5 py-3.5 whitespace-nowrap z-10 shadow-[1px_0_0_0_#f3f4f6] group-hover:shadow-[1px_0_0_0_#e5e7eb] transition-shadow" data-id="element-79">
                  <div className="flex items-center" data-id="element-80">
                    <div className="h-9 w-9 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold text-sm mr-3 ring-1 ring-blue-100" data-id="element-81">
                      {lead.firstName[0]}
                      {lead.lastName[0]}
                    </div>
                    <div data-id="element-82">
                      <div className="font-medium text-gray-900" data-id="element-83">
                        {lead.firstName} {lead.lastName}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5" data-id="element-84">
                        {lead.id}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-85">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${getStageColor(lead.stage)}`} data-id="element-86">
                    {lead.stage}
                  </span>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-87">
                  <div className="flex items-center" data-id="element-88">
                    <span className={`text-sm font-medium ${lead.daysInStage > 10 ? 'text-red-600' : lead.daysInStage > 5 ? 'text-amber-600' : 'text-gray-900'}`} data-id="element-89">
                      {lead.daysInStage} days
                    </span>
                    {lead.daysInStage > 10 && <AlertCircle className="w-4 h-4 text-red-500 ml-1.5" data-id="element-90" />}
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-91">
                  <div className="flex items-center text-sm text-gray-900" data-id="element-92">
                    <Activity className="w-4 h-4 text-gray-400 mr-2" data-id="element-93" />
                    {lead.nextAction}
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-600" data-id="element-94">
                  {formatDate(lead.leadDate)}
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-600" data-id="element-95">
                  {formatDate(lead.applicationDate)}
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap text-sm text-gray-600" data-id="element-96">
                  {formatDate(lead.lastContactDate)}
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-97">
                  <div className="text-sm text-gray-900 font-medium" data-id="element-98">
                    {lead.loanType}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5" data-id="element-99">
                    {lead.loanProgram} • {lead.loanTerm}yr
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-100">
                  <div className="text-sm text-gray-900 font-medium" data-id="element-101">
                    {formatCurrency(lead.loanAmount)}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5" data-id="element-102">
                    {lead.purchasePrice ? `PP: ${formatCurrency(lead.purchasePrice)}` : 'Refi'}{' '}
                    • LTV: {lead.ltv}% • FICO: {lead.ficoScore}
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-103">
                  <div className="flex items-center text-sm text-gray-600 max-w-[200px] truncate" data-id="element-104">
                    <Home className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" data-id="element-105" />
                    <span className="truncate" data-id="element-106">{lead.subjectProperty}</span>
                  </div>
                </td>

                <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-107">
                  <div className="text-sm text-gray-900" data-id="element-108">
                    {lead.referralSourceName}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5" data-id="element-109">
                    {lead.referralSourceContact}
                  </div>
                </td>
              </tr>)}
        </tbody>
      </table>
    </div>;
};